import type { CSSProperties } from 'react';

/**
 * Per-rule glyphs so an operator can tell rules apart at a glance — used in the
 * rules sidebar, the live alert feed and the map pin. Keyed by the rule `id`
 * (the contract shared by {@link Rule} and {@link Anomaly.ruleId}); unknown ids
 * fall back to a generic alert glyph so new rules degrade gracefully.
 *
 * Icons are stroke-only on a 24×24 grid and draw in `currentColor`, so callers
 * set the color (always the severity color from `@soc/shared` — never hardcode).
 */
type Glyph = (props: { size: number }) => JSX.Element;

const svg = (size: number, children: JSX.Element) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden
  >
    {children}
  </svg>
);

/** Globe + meridians — foreign / spoofed serving MCC (country code). */
const globe: Glyph = ({ size }) =>
  svg(
    size,
    <>
      <circle cx="12" cy="12" r="9" />
      <path d="M3 12h18" />
      <ellipse cx="12" cy="12" rx="4" ry="9" />
    </>,
  );

/** Cell grid — invalid tracking-area (TAC) / location-area (LAC) code. */
const grid: Glyph = ({ size }) =>
  svg(
    size,
    <>
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <path d="M3 9h18M3 15h18M9 3v18M15 3v18" />
    </>,
  );

/** Diverging swap arrows — one site jumping between TAC/LAC codes. */
const jump: Glyph = ({ size }) =>
  svg(
    size,
    <>
      <path d="M4 8h13l-3-3M4 8l3 3" />
      <path d="M20 16H7l3-3M20 16l-3 3" />
    </>,
  );

/** Rising signal bars + a transition arrow — radio-tech change mid-session. */
const techShift: Glyph = ({ size }) =>
  svg(
    size,
    <>
      <path d="M4 20V14M9 20V11M14 20V8M19 20V5" />
      <path d="M4 6h11l-2.5-2.5M15 6l-2.5 2.5" />
    </>,
  );

/** Map-pin (official location) + a dashed target dot offset from it — the
 * measured center drifting from the site's official position. */
const pinDrift: Glyph = ({ size }) =>
  svg(
    size,
    <>
      <path d="M8 10a4 4 0 1 1 8 0c0 3-4 7-4 7s-4-4-4-7z" />
      <circle cx="12" cy="10" r="1.2" />
      <circle cx="19" cy="19" r="2.2" strokeDasharray="2 2" />
    </>,
  );

/** Generic triangle-alert fallback for rules without a dedicated glyph. */
const alert: Glyph = ({ size }) =>
  svg(
    size,
    <>
      <path d="M12 3 2 20h20L12 3z" />
      <path d="M12 10v4" />
      <path d="M12 17.5h.01" />
    </>,
  );

const RULE_GLYPHS: Record<string, Glyph> = {
  'mcc-anomaly': globe,
  'tac-lac-anomaly': grid,
  'tac-lac-jump': jump,
  'cell-location-mismatch': pinDrift,
  'site-tech-mismatch': techShift,
};

/** A rule's glyph in `currentColor`. Set `color` on a parent or via `style`. */
export function RuleIcon({
  ruleId,
  size = 16,
  color,
  style,
}: {
  ruleId: string;
  size?: number;
  color?: string;
  style?: CSSProperties;
}) {
  const Glyph = RULE_GLYPHS[ruleId] ?? alert;
  return (
    <span style={{ display: 'inline-flex', color, lineHeight: 0, ...style }}>
      <Glyph size={size} />
    </span>
  );
}
